import { lensById } from '@sleepstation/utils'
import {
  compose,
  equals,
  find,
  lensPath,
  lensProp,
  propEq,
  set,
  view,
} from 'ramda'
import gql from 'graphql-tag'
import { isNotNil } from 'ramda-adjunct'
import pipe from 'ramda/src/pipe'
import React, { useContext, useLayoutEffect } from 'react'
import { useLoaderData } from 'react-router'
import { useMutation, useQuery } from 'urql'
import ENROLMENT_META_KEY from '../../../const/enrolmentMetaKey'
import RoutingContext from '../../../context/RoutingContext'
import UserContext from '../../../context/UserContext'
import { CreateEnrolmentMetaMutation } from '../../../graphql/documents'
import PageNavContainer from '../../../state/PageNavContainer'
import OldAppHeader from '../../AppModule/App/OldAppHeader'
import HubPage from './HubPage'
import LoadOverlay from '../../shared/LoadOverlay'

// -----------------------------------------------------------------------------
// Query
// -----------------------------------------------------------------------------

const CurrentEnrolmentForQuestionnaireResults = gql`
  query questionnaireResults {
    currentEnrolment {
      id
      latestEpworthResult: latestQuestionnaireResult(key: "epworth") {
        id
        maximumScore
        result
        score
      }
      latestDbasResult: latestQuestionnaireResult(key: "dbas") {
        id
        maximumScore
        result
        score
      }
    }
  }
`

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const PAGE_ID = `hub`

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const projectionsSleepReportResultVariationLens = lensPath([
  `projections`,
  `sleepReport`,
  `result`,
  `variation`,
])

const getProjectionsSleepReportResultVariation = view(
  projectionsSleepReportResultVariationLens
)

const getCurrentEnrolmentWillBeRejected = pipe(
  getProjectionsSleepReportResultVariation,
  equals(`hard_reject`)
)

const getCurrentEnrolmentHasSiteWhichAcceptsSelfReferrals = (
  currentEnrolment
) => currentEnrolment.hasSiteWhichAcceptsSelfReferrals

const getCurrentEnrolmentCanSubmitReferralRequest = (
  enrolmentHasOpenReferralRequest,
  enrolmentHasSiteWhichAcceptsSelfReferrals
) =>
  !enrolmentHasOpenReferralRequest && enrolmentHasSiteWhichAcceptsSelfReferrals

const getCurrentEnrolmentHasReferralRequest = (currentEnrolment) => {
  const { nodes } = currentEnrolment.referralRequests
  return nodes.length > 0
}

const getCurrentEnrolmentHasOpenReferralRequest = (currentEnrolment) => {
  const { nodes } = currentEnrolment.referralRequests
  return nodes.some((node) => node.status === `open`)
}

const getHasUserDismissedNotice = (meta) => {
  const metaItem = find(
    propEq(
      ENROLMENT_META_KEY.ONBOARDING_HUB_REJECTION_NOTIFICATION_DISMISSED,
      `key`
    ),
    meta.nodes
  )

  return isNotNil(metaItem) && metaItem.value === `true`
}

const setPagesHubSleepCoachesCardLinkUrl = set(
  compose(
    lensPath([`page`, `content`]),
    lensProp(`sections`),
    lensById(`message-us`),
    lensProp(`cards`),
    lensById(`sleep-coaches`),
    lensPath([`link`, `url`])
  )
)

const injectRouting = (routing, content) =>
  pipe(setPagesHubSleepCoachesCardLinkUrl(routing.chatMessagesPath))(content)

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const HubModule = () => {
  const loaderData = useLoaderData()
  const [, setNav] = PageNavContainer.useContainer()
  const [, createEnrolmentMeta] = useMutation(CreateEnrolmentMetaMutation)
  const { currentUser, currentEnrolment } = useContext(UserContext)
  const { appRouting } = useContext(RoutingContext)
  const loaderDataWithRouting = injectRouting(appRouting, loaderData)
  const [result] = useQuery({
    query: CurrentEnrolmentForQuestionnaireResults,
    variables: [],
  })

  const { data, error } = result

  const { id: enrolmentId } = currentEnrolment

  const { canAccessProgramme, licensorPrefersSelfFunding } = currentEnrolment

  const enrolmentHasSiteWhichAcceptsSelfReferrals =
    getCurrentEnrolmentHasSiteWhichAcceptsSelfReferrals(currentEnrolment)

  const willUserBeRejected = getCurrentEnrolmentWillBeRejected(currentEnrolment)

  const enrolmentHasReferralRequest =
    getCurrentEnrolmentHasReferralRequest(currentEnrolment)

  const enrolmentHasOpenReferralRequest =
    getCurrentEnrolmentHasOpenReferralRequest(currentEnrolment)

  const enrolmentCanSubmitReferralRequest =
    getCurrentEnrolmentCanSubmitReferralRequest(
      enrolmentHasOpenReferralRequest,
      enrolmentHasSiteWhichAcceptsSelfReferrals
    )

  const hasUserDismissedNotice = getHasUserDismissedNotice(
    currentEnrolment.meta
  )

  const isNoticeActive = willUserBeRejected && !hasUserDismissedNotice

  useLayoutEffect(() => {
    setNav({
      pageId: PAGE_ID,
      Header: OldAppHeader,
      pageBackgroundColour: `insetAlt`,
    })
  }, [setNav])

  const handleNoticeClose = () => {
    createEnrolmentMeta({
      params: {
        enrolmentId,
        key: ENROLMENT_META_KEY.ONBOARDING_HUB_REJECTION_NOTIFICATION_DISMISSED,
        value: `true`,
      },
    })
  }

  return !data || error ? (
    <LoadOverlay />
  ) : (
    <HubPage
      currentUser={currentUser}
      enrolmentCategoryType={currentEnrolment?.categoryType}
      currentEnrolmentQuestionnaires={data.currentEnrolment}
      content={loaderDataWithRouting.page.content}
      isNoticeActive={isNoticeActive}
      onNoticeClose={handleNoticeClose}
      canAccessProgramme={canAccessProgramme}
      willUserBeRejected={willUserBeRejected}
      enrolmentCanSubmitReferralRequest={enrolmentCanSubmitReferralRequest}
      enrolmentHasOpenReferralRequest={enrolmentHasOpenReferralRequest}
      enrolmentHasReferralRequest={enrolmentHasReferralRequest}
      licensorPrefersSelfFunding={licensorPrefersSelfFunding}
    />
  )
}

export default HubModule
